import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import { Box, TextField, Select, Button, IconButton, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { BsTrash } from "react-icons/bs";
import { ImageIcon, X } from "lucide-react";
import { getInstalacion, updateInstalacion, getAlmacenes, getProductosKaeAlmacen, getProductosKae } from "../../../../services";
import { comprimirImagen, comprimirImagenOld } from "../../../../functions/funciones";
import LaeButton from "../../../../components/Buttons/LaeButton";
import { UsuarioContext } from "../../../../providers/userContext";

const MAX_IMAGENES = 10;
const MAX_TAMANO_BYTES = 10 * 1024 * 1024;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "90vw", md: "85ch" },
  maxWidth: "85ch",
  maxHeight: { xs: "92vh", sm: "85vh" },
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  border: { xs: "1px solid #000", sm: "2px solid #000" },
  boxShadow: 24,
};

const ESTADOS_KAE = [
  { id: 1, key: "Activa" },
  { id: 4, key: "Baja" },
  { id: 5, key: "Anulado" },
];

const EditarInstalacion = ({ instalacionId, opened, handleClose, onSaved }) => {
  const { t } = useTranslation(["common"]);
  const user = useContext(UsuarioContext);
  const puedeEditarFechaFactura = user?.rol === "ADMIN" || user?.rol === "VENT";
  const puedeEditarEstado = user?.rol === "ADMIN" || user?.rol === "VENT";
  // El tecnico puede editar la instalacion pero no decidir que señales son obligatorias
  const puedeEditarSenales = user?.rol === "ADMIN" || user?.rol === "VENT";
  const [observaciones, setObservaciones] = useState("");
  const [senalesObligatorias, setSenalesObligatorias] = useState([]);
  const [senalesMarcadas, setSenalesMarcadas] = useState([]);
  const [fechaInstalacion, setFechaInstalacion] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceDate2, setInvoiceDate2] = useState("");
  const [estadoId, setEstadoId] = useState(1);
  const [loading, setLoading] = useState(false);
  const [instalacionData, setInstalacionData] = useState(null);
  const [listaItems, setListaItems] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [productosCache, setProductosCache] = useState({});
  const [imagenesExistentes, setImagenesExistentes] = useState([]);
  const [imagenesABorrar, setImagenesABorrar] = useState([]);
  const [imagenesNuevas, setImagenesNuevas] = useState([]);
  const [procesandoImagenes, setProcesandoImagenes] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [imagenesCargadas, setImagenesCargadas] = useState(0);
  const fileInputRef = useRef(null);

  const todasImagenesListas = imagenesExistentes.length === 0 || imagenesCargadas >= imagenesExistentes.length;
  const bloqueado = loading || !todasImagenesListas;

  useEffect(() => {
    if (opened && instalacionId) {
      fetchData();
    }
    if (!opened) {
      imagenesNuevas.forEach((img) => img.url && URL.revokeObjectURL(img.url));
      setImagenesNuevas([]);
      setImagenesABorrar([]);
      setImagenesExistentes([]);
      setImagenesCargadas(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, instalacionId]);

  useEffect(() => {
    return () => {
      imagenesNuevas.forEach((img) => img.url && URL.revokeObjectURL(img.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [data, almRes] = await Promise.all([
        getInstalacion(instalacionId),
        getAlmacenes(),
      ]);
      setInstalacionData(data);
      setObservaciones(data?.observaciones || "");
      setFechaInstalacion(data?.fecha_instalacion ? data.fecha_instalacion.substring(0, 10) : "");
      setInvoiceDate(data?.invoiceDate ? data.invoiceDate.substring(0, 10) : "");
      setInvoiceDate2(data?.invoiceDate2 ? data.invoiceDate2.substring(0, 10) : "");
      setEstadoId(data?.estado_id || 1);
      setAlmacenes(Array.isArray(almRes?.data) ? almRes.data : []);

      const catalogo = await getProductosKae();
      const productosCatalogo = Array.isArray(catalogo) ? catalogo : (catalogo?.data ?? []);
      setSenalesObligatorias(productosCatalogo.filter((p) => p.obligatorio));
      setSenalesMarcadas(
        (data?.productos || []).filter((p) => p.obligatorio).map((p) => p.producto_id)
      );

      // Solo las filas con almacen son productos instalados; el resto son señales pendientes
      const items = (data?.productos || []).filter((p) => p.almacen_id).map((p) => ({
        id: p.id,
        producto_id: p.producto_id,
        almacen_id: p.almacen_id,
        nombre: p.producto?.nombre || "",
        cantidad: p.cantidad,
        cantidadOriginal: p.cantidad,
        stockDisponible: 0,
        isNew: false,
      }));

      // Cargar stock disponible para cada almacén existente
      const almacenIds = [...new Set(items.map((i) => i.almacen_id).filter(Boolean))];
      const cache = {};
      for (const aId of almacenIds) {
        const res = await getProductosKaeAlmacen(aId);
        cache[aId] = Array.isArray(res) ? res : [];
      }
      setProductosCache(cache);

      // Calcular stock disponible para cada item
      const itemsConStock = items.map((item) => {
        const productos = cache[item.almacen_id] || [];
        const prod = productos.find((p) => p.id === item.producto_id);
        const stockActual = prod?.stocks?.[0]?.cantidad || 0;
        return { ...item, stockDisponible: stockActual + item.cantidadOriginal };
      });

      setListaItems(itemsConStock);

      const imgs = (data?.imagenes || []).map((img) => ({
        id: img.id,
        ruta: img.ruta,
        url: `${process.env.REACT_APP_STORAGE_URL}${img.ruta}`,
      }));
      setImagenesCargadas(0);
      setImagenesExistentes(imgs);
      setImagenesABorrar([]);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const totalImagenes = imagenesExistentes.length + imagenesNuevas.length;

  const onClickAddImagenes = () => {
    if (procesandoImagenes || enviando) return;
    fileInputRef.current?.click();
  };

  const esMovil = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const esMovilUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const esPantallaPequena = window.innerWidth <= 768 || window.innerHeight <= 600;
    const esTactil = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const indicadores = [esMovilUserAgent, esPantallaPequena, esTactil].filter(Boolean).length;
    return indicadores >= 2;
  };

  // Mismo patrón que CrearHojaVisita/EditarHojaVisita: Resizer primero con
  // timeout de 20s; si falla o tarda, fallback a canvas.toBlob con timeout 10s.
  const comprimirConTimeout = (file) => {
    const comprimirImagenOldConTimeout = (f) => {
      return new Promise((resolve, reject) => {
        let terminado = false;
        const timerOld = setTimeout(() => {
          if (!terminado) {
            terminado = true;
            reject(new Error("Timeout en compresión antigua"));
          }
        }, 10000);
        comprimirImagenOld(f)
          .then((blob) => {
            if (!terminado) {
              terminado = true;
              clearTimeout(timerOld);
              resolve(blob);
            }
          })
          .catch((err) => {
            terminado = true;
            clearTimeout(timerOld);
            reject(err);
          });
      });
    };

    return new Promise(async (resolve, reject) => {
      let terminado = false;
      const timer = setTimeout(async () => {
        if (!terminado) {
          terminado = true;
          try {
            const blobOld = await comprimirImagenOldConTimeout(file);
            resolve(blobOld);
          } catch (err) {
            reject(err);
          }
        }
      }, 20000);

      try {
        const blob = await comprimirImagen(file);
        if (!terminado) {
          terminado = true;
          clearTimeout(timer);
          resolve(blob);
        }
      } catch (err) {
        clearTimeout(timer);
        try {
          const blobOld = await comprimirImagenOldConTimeout(file);
          resolve(blobOld);
        } catch (errOld) {
          reject(errOld);
        }
      }
    });
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const disponibles = Math.max(0, MAX_IMAGENES - totalImagenes);
    if (disponibles === 0) {
      toast.error(t("installations.images.tooManyImages", { max: MAX_IMAGENES }));
      return;
    }
    const aProcesar = files.slice(0, disponibles);
    if (files.length > aProcesar.length) {
      toast.warn(t("installations.images.tooManyImages", { max: MAX_IMAGENES }));
    }

    const dispositivoMovil = esMovil();
    const archivosValidos = aProcesar.filter((file) => {
      if (!file.type?.startsWith("image/")) {
        toast.error(t("installations.images.invalidType", { name: file.name }));
        return false;
      }
      if (file.size > MAX_TAMANO_BYTES) {
        toast.error(t("installations.images.tooLarge", { name: file.name }));
        return false;
      }
      return true;
    });
    if (archivosValidos.length === 0) return;

    setProcesandoImagenes(true);
    const nuevas = [];

    const procesarArchivo = async (file) => {
      try {
        let finalFile = file;
        if (file.size > 500 * 1024) {
          const blob = await comprimirConTimeout(file);
          const nombreArchivo = file.name.split('.').slice(0, -1).join('.') + '.webp';
          finalFile = new File([blob], nombreArchivo, { type: 'image/webp' });
        }
        nuevas.push({ file: finalFile, url: URL.createObjectURL(finalFile), name: finalFile.name });
      } catch (err) {
        console.error("Error procesando imagen", file.name, err);
        toast.error(t("installations.images.processingError", { name: file.name }));
      }
    };

    try {
      if (dispositivoMovil) {
        // Móvil: secuencial — menos picos de RAM en dispositivos flojos.
        for (let i = 0; i < archivosValidos.length; i++) {
          await procesarArchivo(archivosValidos[i]);
        }
      } else {
        // PC: en paralelo — más rápido.
        await Promise.all(archivosValidos.map((file) => procesarArchivo(file)));
      }
    } finally {
      if (nuevas.length) {
        setImagenesNuevas((prev) => [...prev, ...nuevas]);
      }
      setProcesandoImagenes(false);
    }
  };

  const removeImagenNueva = (idx) => {
    setImagenesNuevas((prev) => {
      const copia = [...prev];
      const [eliminada] = copia.splice(idx, 1);
      if (eliminada?.url) URL.revokeObjectURL(eliminada.url);
      return copia;
    });
  };

  const removeImagenExistente = (id) => {
    setImagenesExistentes((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item) setImagenesABorrar((prevDel) => [...prevDel, { id: item.id, ruta: item.ruta }]);
      return prev.filter((x) => x.id !== id);
    });
  };

  const cargarProductosAlmacen = async (almacenId) => {
    if (productosCache[almacenId]) return productosCache[almacenId];
    try {
      const res = await getProductosKaeAlmacen(almacenId);
      const items = Array.isArray(res) ? res : [];
      setProductosCache((prev) => ({ ...prev, [almacenId]: items }));
      return items;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getProductosDisponibles = (currentIndex) => {
    const item = listaItems[currentIndex];
    if (!item.almacen_id) return [];
    const productos = productosCache[item.almacen_id] || [];
    return productos.filter(
      (p) => !listaItems.some((s, i) => i !== currentIndex && Number(s.almacen_id) === Number(item.almacen_id) && Number(s.producto_id) === p.id)
    );
  };

  const handleAddItem = () => {
    setListaItems([
      ...listaItems,
      { almacen_id: "", producto_id: "", nombre: "", cantidad: "", cantidadOriginal: 0, stockDisponible: 0, isNew: true },
    ]);
  };

  const handleRemoveItem = (index) => {
    setListaItems(listaItems.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    const updated = [...listaItems];
    if (name === "cantidad") {
      const max = updated[index].stockDisponible || Infinity;
      const num = value === "" ? "" : Math.max(0, Math.min(Number(value), max));
      updated[index] = { ...updated[index], [name]: num };
    } else {
      updated[index] = { ...updated[index], [name]: Number(value) };
    }
    setListaItems(updated);
  };

  const handleSelectAlmacen = async (index, event) => {
    const almacenId = event.target.value;
    const updated = [...listaItems];
    updated[index] = { ...updated[index], almacen_id: almacenId, producto_id: "", nombre: "", cantidad: "", stockDisponible: 0 };
    setListaItems(updated);
    if (almacenId) {
      await cargarProductosAlmacen(almacenId);
    }
  };

  const handleSelectProducto = (index, event) => {
    const productoId = parseInt(event.target.value);
    const item = listaItems[index];
    const productos = productosCache[item.almacen_id] || [];
    const producto = productos.find((p) => p.id === productoId);
    const updated = [...listaItems];
    updated[index] = {
      ...updated[index],
      producto_id: productoId,
      nombre: producto?.nombre || "",
      stockDisponible: producto?.stocks?.[0]?.cantidad || 0,
    };
    setListaItems(updated);
  };

  const handleSubmit = async () => {
    if (enviando) return;
    const toastInfo = toast.info(t("installations.messages.saving"), { autoClose: false });
    setEnviando(true);
    try {
      const productos = listaItems
        .filter((item) => item.producto_id && item.almacen_id && item.cantidad > 0)
        .map((item) => ({
          producto_id: item.producto_id,
          almacen_id: Number(item.almacen_id),
          cantidad: Number(item.cantidad),
        }));

      const payload = {
        observaciones,
        productos,
        ...(puedeEditarSenales && { senalesObligatorias: senalesMarcadas }),
      };

      if (fechaInstalacion) {
        payload.fecha_instalacion = fechaInstalacion;
      }

      if (puedeEditarFechaFactura) {
        payload.invoiceDate = invoiceDate || null;
        payload.invoiceDate2 = invoiceDate2 || null;
      }

      if (puedeEditarEstado) {
        payload.estado_id = Number(estadoId);
      }

      const result = await updateInstalacion(
        instalacionId,
        payload,
        imagenesNuevas.map((i) => i.file),
        imagenesABorrar
      );
      if (result.status === 200) {
        toast.update(toastInfo, {
          render: t("installations.messages.editedSuccess"),
          type: toast.TYPE.SUCCESS,
          autoClose: true,
        });
        if (onSaved) onSaved();
        else handleClose();
      } else {
        const msg = result.data?.error === "ERROR_STOCK_INSUFICIENTE"
          ? t("installations.messages.stockInsuficiente")
          : t("installations.messages.editError");
        toast.update(toastInfo, {
          render: msg,
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
      }
    } catch (err) {
      console.log(err);
      toast.update(toastInfo, {
        render: t("installations.messages.editError"),
        type: toast.TYPE.ERROR,
        autoClose: true,
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={opened}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box sx={style}>
        {/* Header fijo */}
        <Box sx={{ padding: { xs: "12px 14px 0", sm: "16px 32px 0" } }}>
          <h2 style={{ marginTop: 0 }}>{t("installations.edit")}</h2>
          {instalacionData && (
            <p style={{ fontSize: "14px", color: "#666", marginBottom: 16 }}>
              {instalacionData.propertie?.razonSocial} — {instalacionData.propertie?.nif}
            </p>
          )}
        </Box>

        {/* Contenido scrollable */}
        <Box sx={{ padding: { xs: "0 14px", sm: "0 32px" }, overflowY: "auto", flex: 1, position: "relative" }}>
          {bloqueado && (
            <Box
              sx={{
                position: "absolute", inset: 0, zIndex: 5,
                backgroundColor: "rgba(255,255,255,0.7)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexDirection: "column", gap: 1.5,
              }}
            >
              <CircularProgress style={{ color: "#92004D" }} />
              <Box sx={{ fontSize: 13, color: "#666" }}>
                {loading ? t("installations.messages.loading", { defaultValue: "Cargando datos..." }) : t("installations.images.loadingImages", { defaultValue: "Cargando imágenes..." })}
              </Box>
            </Box>
          )}
          <fieldset disabled={bloqueado} style={{ border: "none", padding: 0, margin: 0, opacity: bloqueado ? 0.5 : 1, pointerEvents: bloqueado ? "none" : "auto" }}>

          <Box mb={2} sx={{paddingTop: 2}} display="flex" flexDirection="row" gap={2}>
            <TextField
              type="date"
              label={t("installations.columns.performedDate")}
              variant="outlined"
              value={fechaInstalacion}
              onChange={(e) => setFechaInstalacion(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={puedeEditarEstado ? { flex: 1 } : undefined}
            />
            {puedeEditarEstado && (
              <TextField
                select
                SelectProps={{ native: true }}
                label={t("filters.state", { defaultValue: "Estado" })}
                variant="outlined"
                value={estadoId}
                onChange={(e) => setEstadoId(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ flex: 1 }}
              >
                {ESTADOS_KAE.map((est) => (
                  <option key={est.id} value={est.id}>
                    {t(`stateNames.${est.key}`, { defaultValue: est.key })}
                  </option>
                ))}
              </TextField>
            )}
          </Box>

          {puedeEditarFechaFactura && (
            <Box mb={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                type="date"
                label={t("installations.columns.invoiceDate")}
                variant="outlined"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label={t("installations.columns.invoiceDate2")}
                variant="outlined"
                value={invoiceDate2}
                onChange={(e) => setInvoiceDate2(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          )}

          <h3 style={{ marginBottom: 8 }}>{t("installations.detail.products")}</h3>
          {listaItems.length === 0 ? (
            <div style={{ margin: 15 }}>
              <p>{t("products.messages.noProducts")}</p>
            </div>
          ) : (
            listaItems.map((item, index) => (
              <div key={index}>
                <Box
                  mx={1}
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  gap={{ xs: 1, sm: 0 }}
                  mb={{ xs: 2, sm: 0 }}
                >
                  {item.isNew ? (
                    <Select
                      native
                      name="almacen_id"
                      sx={{ width: { xs: "100%", sm: "25%" }, margin: { xs: 0, sm: "5px" } }}
                      value={item.almacen_id || ""}
                      onChange={(e) => handleSelectAlmacen(index, e)}
                    >
                      <option value="">{t("almacenes.title")}</option>
                      {almacenes.map((a) => (
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </Select>
                  ) : (
                    <Box sx={{ width: { xs: "100%", sm: "25%" }, margin: { xs: 0, sm: "5px" }, fontSize: "13px", color: "#666" }}>
                      {almacenes.find((a) => a.id === item.almacen_id)?.nombre || item.almacen_id}
                    </Box>
                  )}
                  {item.isNew ? (
                    <Select
                      native
                      name="producto_id"
                      sx={{ width: { xs: "100%", sm: "35%" }, margin: { xs: 0, sm: "5px" } }}
                      value={item.producto_id || ""}
                      onChange={(e) => handleSelectProducto(index, e)}
                      disabled={!item.almacen_id}
                    >
                      <option value="">{t("products.titleSingular")}</option>
                      {getProductosDisponibles(index).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre} (stock: {p.stocks?.[0]?.cantidad || 0})
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Box sx={{ width: { xs: "100%", sm: "35%" }, margin: { xs: 0, sm: "5px" }, fontSize: "14px", fontWeight: "bold" }}>
                      {item.nombre}
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <TextField
                      sx={{ width: { xs: "100%", sm: 110 }, margin: { xs: 0, sm: "5px" } }}
                      type="number"
                      label={t("products.columns.cuantity")}
                      variant="outlined"
                      name="cantidad"
                      onChange={(e) => handleInputChange(index, e)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 0, max: item.stockDisponible || undefined }}
                      value={item.cantidad}
                    />
                    <span style={{ margin: "0 8px", fontSize: "12px", color: "#888", whiteSpace: "nowrap" }}>
                      / {item.stockDisponible}
                    </span>
                    <button
                      onClick={() => handleRemoveItem(index)}
                      style={{ backgroundColor: "transparent", color: "#000", height: 30, border: "none", cursor: "pointer" }}
                    >
                      <BsTrash />
                    </button>
                  </Box>
                </Box>
              </div>
            ))
          )}
          <Box mx={2} my={2} display="flex" flexDirection="row">
            <button
              className="btn-Secondary"
              style={{ color: "white", padding: "6px 12px", border: "none", borderRadius: "4px", cursor: "pointer" }}
              onClick={handleAddItem}
            >
              {t("actions.add")} {t("products.titleSingular").toLowerCase()}
            </button>
          </Box>

          <Box mb={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label={t("installations.detail.observations")}
              variant="outlined"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          </Box>

          {senalesObligatorias.length > 0 && (
            <Box mb={2}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {t("installations.mandatorySignage")}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 1 }}>
                {t("installations.mandatorySignageHint")}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 220, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }}>
                {senalesObligatorias.map((senal) => (
                  <Box key={senal.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <input
                      type="checkbox"
                      id={`senal-${senal.id}`}
                      checked={senalesMarcadas.includes(senal.id)}
                      disabled={bloqueado || !puedeEditarSenales}
                      onChange={() =>
                        setSenalesMarcadas((previas) =>
                          previas.includes(senal.id)
                            ? previas.filter((s) => s !== senal.id)
                            : [...previas, senal.id]
                        )
                      }
                      style={{ width: "auto", margin: 0 }}
                    />
                    <label htmlFor={`senal-${senal.id}`} style={{ margin: 0, cursor: "pointer" }}>
                      {senal.sku} - {senal.nombre}
                    </label>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box mb={2}>
            <Box
              display="flex"
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={1}
              mb={1}
            >
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                {t("installations.images.title")} ({totalImagenes}/{MAX_IMAGENES})
              </span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ImageIcon size={16} />}
                onClick={onClickAddImagenes}
                disabled={procesandoImagenes || totalImagenes >= MAX_IMAGENES}
                sx={{ color: "#92004D", borderColor: "#92004D", alignSelf: { xs: "stretch", sm: "auto" }, "&:hover": { borderColor: "#92004D", backgroundColor: "rgba(146,0,77,0.04)" } }}
              >
                {procesandoImagenes ? t("installations.images.processing") : t("installations.images.addButton")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleFilesSelected}
              />
            </Box>
            {(imagenesExistentes.length + imagenesNuevas.length) > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {imagenesExistentes.map((img) => (
                  <Box
                    key={`exist-${img.id}`}
                    sx={{ position: "relative", width: { xs: 80, sm: 90 }, height: { xs: 80, sm: 90 }, borderRadius: 1, overflow: "hidden", border: "1px solid #e0e0e0" }}
                  >
                    <img
                      src={img.url}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onLoad={() => setImagenesCargadas((c) => c + 1)}
                      onError={() => setImagenesCargadas((c) => c + 1)}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeImagenExistente(img.id)}
                      sx={{
                        position: "absolute", top: 2, right: 2,
                        backgroundColor: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                      }}
                    >
                      <X size={12} />
                    </IconButton>
                  </Box>
                ))}
                {imagenesNuevas.map((img, idx) => (
                  <Box
                    key={`new-${idx}`}
                    sx={{ position: "relative", width: { xs: 80, sm: 90 }, height: { xs: 80, sm: 90 }, borderRadius: 1, overflow: "hidden", border: "2px solid #92004D" }}
                  >
                    <img src={img.url} alt={img.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <IconButton
                      size="small"
                      onClick={() => removeImagenNueva(idx)}
                      sx={{
                        position: "absolute", top: 2, right: 2,
                        backgroundColor: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                      }}
                    >
                      <X size={12} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          </fieldset>
        </Box>

        {/* Botones fijos abajo */}
        <Box
          sx={{
            padding: { xs: "10px 14px 14px", sm: "12px 32px 16px" },
            borderTop: "1px solid #e0e0e0",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridGap: "15px",
            gap: "10px",
            fontSize: "12px",
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "whitesmoke",
              color: "#92004D",
              ":hover": { backgroundColor: "#92004D", color: "white" },
              p: 1,
            }}
            disableElevation
            onClick={handleClose}
          >
            {t("actions.cancel")}
          </Button>
          <LaeButton onClick={handleSubmit} disabled={bloqueado || enviando || procesandoImagenes}>
            {t("actions.save")}
          </LaeButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditarInstalacion;
